<script setup lang="ts">
/**
 * /resume-examples/<slug> — one profession or situation: the answer, a
 * complete sample resume (rendered and as text), summaries per level, skills,
 * bullets before and after, credentials, the template, and the FAQ.
 *
 * The content is one JSON file per page (data/resume-examples, Czech in
 * data/resume-examples/cs), imported lazily so a page ships only its own
 * example. The index in constants/resumeExamples.ts decides which slugs exist.
 */
import { defaultLocaleSlug } from '~/constants/appLocale'
import { findResumeExample, roleName, type ResumeExampleEntry, type ResumeExampleIndexItem } from '~/constants/resumeExamples'
import { loadResumeExampleEntry } from '~/utils/resumeExampleEntries'

const { t, locale } = useI18n()
const route = useRoute()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/priklady-zivotopisu/[slug]', de: '/lebenslauf-beispiele/[slug]', pl: '/przyklady-cv/[slug]' } })

// The English slug: under /cs and /de the URL carries a translated one.
const slug = defaultLocaleSlug(route.path)
const item = findResumeExample(slug)
const initialEntry = await loadResumeExampleEntry(slug, locale.value)
if (!item || !initialEntry) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}
const entry = shallowRef<ResumeExampleEntry>(initialEntry)
watch(locale, async (lang) => {
  const next = await loadResumeExampleEntry(slug, lang)
  if (next) entry.value = next
})

const path = `/resume-examples/${slug}`
const role = computed(() => roleName(item, locale.value))

// The year only where the ranking titles carry one (see the index), and always
// the current one, so the title never goes stale in January.
const seoTitle = computed(() => item.yearInTitle
  ? t('resumeExamples.page.seoTitleYear', { role: role.value, year: new Date().getFullYear() })
  : t('resumeExamples.page.seoTitle', { role: role.value }))

// The questions answered on the page, in the same words, mirrored into FAQPage.
const faqItems = computed(() => entry.value.faq.map((faq, index) => ({ id: `q${index + 1}`, ...faq })))

const related = computed(() => entry.value.related
  .map(relatedSlug => findResumeExample(relatedSlug))
  .filter((relatedItem): relatedItem is ResumeExampleIndexItem => Boolean(relatedItem)))

const extraNodes = computed(() => [faqPageNode(path, entry.value.faq)])

/*
 * The roles UK applicants also take in the NHS or the Civil Service, where the
 * application is scored on a statement rather than the CV: a line to the
 * writer for it (English only, see components/marketing/UkToolsNote.vue).
 */
const UK_NOTES: Record<string, { tools: ('civil-service' | 'nhs')[]; text: string }> = {
  'registered-nurse': { tools: ['nhs'], text: 'For nursing posts on NHS Jobs or Trac, shortlisting scores your supporting information against each criterion of the person specification, not your CV alone.' },
  'cna': { tools: ['nhs'], text: 'In the UK the role is usually a healthcare assistant or healthcare support worker. NHS applications are shortlisted on your supporting information against each criterion of the person specification.' },
  'caregiver': { tools: ['nhs'], text: 'For care roles in the NHS, shortlisting scores your supporting information against each criterion of the person specification, not your CV alone.' },
  'medical-assistant': { tools: ['nhs'], text: 'For clinical support roles in the NHS, shortlisting scores your supporting information against each criterion of the person specification.' },
  'pharmacy-technician': { tools: ['nhs'], text: 'For pharmacy technician posts in the NHS, shortlisting scores your supporting information against each criterion of the person specification.' },
  'dental-assistant': { tools: ['nhs'], text: 'For dental nurse posts in the NHS, shortlisting scores your supporting information against each criterion of the person specification.' },
  'administrative-assistant': { tools: ['civil-service', 'nhs'], text: 'Administrative roles in the Civil Service (AA, AO) and the NHS are scored on statements: behaviour statements and a personal statement, or supporting information against the person specification.' },
  'customer-service': { tools: ['civil-service'], text: 'Customer-facing roles in the UK Civil Service, such as at HMRC or DWP, are scored on behaviour statements and a personal statement as well as your CV.' },
  'human-resources': { tools: ['civil-service', 'nhs'], text: 'HR roles in the Civil Service and the NHS are scored on statements: behaviour statements and a personal statement, or supporting information against the person specification.' },
  'data-analyst': { tools: ['civil-service'], text: 'Analyst roles in the UK Civil Service are scored on behaviour statements and a personal statement against the advert\'s essential criteria.' },
  'project-manager': { tools: ['civil-service'], text: 'Project and delivery roles in the UK Civil Service are scored on behaviour statements, such as Delivering at Pace, and a personal statement.' },
}
const ukNote = UK_NOTES[slug]
</script>

<template>
  <MarketingFeaturePage
    :seo-title="seoTitle"
    :seo-description="entry.seoDescription"
    :path="path"
    :breadcrumb-title="t('resumeExamples.page.heading', { role })"
    :parent="{ name: t('resumeExamples.hub.heading'), path: '/resume-examples' }"
    :extra-nodes="extraNodes"
  >
    <MarketingResumeExampleEntry :key="locale" :entry="entry" :item="item" />

    <MarketingFaqSection
      :title="t('resumeExamples.page.faqTitle', { role })"
      :items="faqItems"
      name="resume-example-faq"
    />

    <MarketingUkToolsNote v-if="ukNote" :tools="ukNote.tools" :text="ukNote.text" />

    <MarketingResumeExampleNext :related="related" />
  </MarketingFeaturePage>
</template>
